import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store'
import { SliceType, setCompanyFields, setProductType } from '../../../store/businessSlice'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import styled from 'styled-components'
import Colors from '../../../assets/styles/Colors'
import Responsive from '../../../assets/config/Responsive'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import ProgressBar from '../../../components/v2/ProgressBar'
import Dropdown from '../../../components/v2/Dropdown'
import Input from '../../../components/v2/Input'
import TextArea from '../../../components/v2/TextArea'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'

const OtherCompanyInfo = () => {
  const navigate = useNavigate()
  //
  const { t } = useTranslation()
  //
  const dispatch = useAppDispatch()
  //
  const { companyInfo } = useAppSelector((data: { business: SliceType }) => data.business)
  //

  // DATA
  const pageData = [
    { id: 1, title: `${t('companyInfo')}`, url: `/other-company-info`, active: true, passed: true },
    { id: 2, title: `${t('contactPersonInfo')}`, url: '/company-contact-info', active: false, passed: false },
  ]
  //
  const headerCount = {
    passedCount: 1,
    totalCount: 2,
  }
  //

  // Dropdown Arrays //
  //
  const insuranceProducts = [
    {
      id: '1',
      label: `${t('cargo')}`,
      value: `${t('cargo')}`,
    },
    {
      id: '2',
      label: `${t('stock')}`,
      value: `${t('stock')}`,
    },
    {
      id: '3',
      label: `${t('forwarderLiability')}`,
      value: `${t('forwarderLiability')}`,
    },
    {
      id: '4',
      label: `${t('renterLiability')}`,
      value: `${t('renterLiability')}`,
    },
    {
      id: '5',
      label: `${t('businessInteruption')}`,
      value: `${t('businessInteruption')}`,
    },
    {
      id: '6',
      label: `${t('professionalLiability')}`,
      value: `${t('professionalLiability')}`,
    },
    {
      id: '7',
      label: `${t('financialLiability')}`,
      value: `${t('financialLiability')}`,
    },
    {
      id: '8',
      label: `${t('agro')}`,
      value: `${t('agro')}`,
    },
    {
      id: '9',
      label: `${t('thirdLiability')}`,
      value: `${t('thirdLiability')}`,
    },
    {
      id: '10',
      label: `${t('machinery')}`,
      value: `${t('machinery')}`,
    },
  ]
  //
  // Dropdown Arrays //

  // VALIDATION
  //
  // english and georgian letter with space and number regex
  const letterNumberRegex = /^[ა-ჰa-zA-Z0-9\s]*$/
  // english and georgian letter with space regex
  const letterSpaceRegex = /^[ა-ჰa-zA-Z\s]*$/
  // space regex
  const spaceRegex = /^\S*$/
  //

  const OtherInfoValidationSchema = Yup.object({
    insuranceProducts: Yup.string().required(`${t('required')}`),
    companyName: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterNumberRegex, `${t('enterGeorgianOrEnglishLetters')}`)
      .required(`${t('required')}`),
    companyID: Yup.string()
      .min(9, `${t('minNineSymbolos')}`)
      .max(9, `${t('minNineSymbolos')}`)
      .matches(spaceRegex, `${t('spaceValidation')}`)
      .required(`${t('required')}`),
    companyActivity: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterSpaceRegex, `${t('enterGeorgianOrEnglishLetters')}`),
    comment: Yup.string().min(2, `${t('minTwoSymbolos')}`),
  })

  const initialValues = {
    companyName: companyInfo.companyName || '',
    companyID: companyInfo.companyID || '',
    companyActivity: companyInfo.companyActivity || '',
    comment: companyInfo.comment || '',
    insuranceProducts: companyInfo.insuranceProduct || '',
  }
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: OtherInfoValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log(values)
    },
  })

  // VALIDATION

  // Button is valid

  const isValid =
    formik.isValid === true && companyInfo.companyName && companyInfo.companyID && companyInfo.insuranceProduct
      ? true
      : false

  // Button is valid

  //   set productType for page and scroll to the  top when rendering
  useEffect(() => {
    dispatch(setProductType('other'))
    window.scrollTo(0, 0)
  }, [dispatch])

  return (
    <Wrapper>
      <ScreenHeader totalCount={headerCount.totalCount} passedCount={headerCount.passedCount} />
      <Content>
        <Title text={t('companyInfo')} />
        <ProgressBar pageObj={pageData} />
        <WhiteBox>
          <Dropdown
            title={t('insuranceProduct')}
            isSearchable={true}
            options={insuranceProducts}
            value={companyInfo.insuranceProduct || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'insuranceProduct',
                  fieldValue: value.value,
                }),
              ),
              formik.setFieldValue('inrsuranceProduct', value.value),
            ]}
          />

          <Input
            id="companyName"
            label={t('companyName')}
            type="text"
            required={true}
            value={companyInfo.companyName || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyName',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyName', value),
            ]}
            width={'fullWidth'}
            error={formik.errors.companyName && companyInfo?.companyName ? true : false}
            errorText={formik.errors.companyName}
            onBlur={formik.handleBlur}
          />

          <Input
            id="company-id"
            label={t('companyID')}
            width={'fullWidth'}
            required={true}
            type="number"
            value={companyInfo.companyID || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyID',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyID', value),
            ]}
            error={formik.errors.companyID && companyInfo?.companyID ? true : false}
            errorText={formik.errors.companyID}
            onBlur={formik.handleBlur}
          />

          <Input
            id="company-field-of-activity"
            label={t('companyActivity')}
            type="text"
            width={'fullWidth'}
            value={companyInfo.companyActivity || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyActivity',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyActivity', value),
            ]}
            error={formik.errors.companyActivity && companyInfo?.companyActivity ? true : false}
            errorText={formik.errors.companyActivity}
            onBlur={formik.handleBlur}
          />

          <Line />

          <TextArea
            id="add-comment"
            label={t('commentAdd')}
            width={'fullWidth'}
            value={companyInfo.comment || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'comment',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('comment', value),
            ]}
            error={formik.errors.comment && companyInfo?.comment ? true : false}
            errorText={formik.errors.comment}
            onBlur={formik.handleBlur}
          />
        </WhiteBox>

        <ButtonBox>
          <NavigationButton buttonType="prev" text={t('back')} onClick={() => navigate('/business-category')} />
          <NavigationButton
            buttonType="next"
            text={t('next')}
            onClick={() => navigate('/company-contact-info')}
            disabled={!isValid}
          />
        </ButtonBox>
      </Content>

      <BackgroundIcon/>
    </Wrapper>
  )
}

export default OtherCompanyInfo

const Wrapper = styled.div`
  min-height: 100vh;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
`

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  position: relative;
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
  
`

const WhiteBox = styled.div`
  margin-top: 32px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: ${Colors.white};
  max-width: 464px;
  width: 100%;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 32px 48px;

  ${Responsive.mobile} {
    padding: 24px 20px;
    max-width: 350px;
    width: 100%;
    margin-top: 26px;
  }
`
const ButtonBox = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 40px;
  button{
    width: 174px;
  }

  ${Responsive.mobile}{
    button{
      width: 140px;
    }
  }
`

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${Colors.secondaryGrey};
`
